/**
 * Analytics Consent
 *
 * Stores the visitor's analytics choice in LocalStorage and notifies
 * subscribers when it changes. Nothing is loaded or sent until consent is
 * explicitly granted — declining (or ignoring the prompt) keeps GA off.
 */

export const CONSENT_STORAGE_KEY = 'analytics-consent'

export type ConsentState = 'granted' | 'denied'

/** Consent decision, or null when the visitor hasn't chosen yet. */
export function getConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : null
  } catch {
    // Private mode / blocked storage: treat as undecided, which means "off"
    return null
  }
}

const listeners = new Set<() => void>()

/** Record a decision and notify subscribers. */
export function setConsent(state: ConsentState): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, state)
  } catch {
    // Ignore storage errors — the in-memory notification below still applies
    // for this session.
  }
  cachedSnapshot = state
  for (const listener of listeners) listener()
}

/** Clear the decision so the visitor is asked again (consent withdrawal). */
export function clearConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY)
  } catch {
    // Ignore storage errors
  }
  cachedSnapshot = null
  for (const listener of listeners) listener()
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function subscribeToConsent(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// useSyncExternalStore requires a stable snapshot value between changes, so the
// LocalStorage read is cached and only refreshed when setConsent runs.
let cachedSnapshot: ConsentState | null | undefined

export function getConsentSnapshot(): ConsentState | null {
  if (cachedSnapshot === undefined) {
    cachedSnapshot = getConsent()
  }
  return cachedSnapshot
}

/** Test helper: drop the cached snapshot so the next read hits storage. */
export function resetConsentCache(): void {
  cachedSnapshot = undefined
}
